<template>
  <div class="waiting-list">
    <div class="waiting-list__header">
      <h1 class="waiting-list__title">Лист ожидания</h1>
      <button class="waiting-list__button" type="button" v-on:click="onCreateWaiting">Добавить запись</button>
    </div>
    <input-text
      placeholder="Поиск"
      v-model="searchQuery"
      class="waiting-list__search"
    />
    <side-slide-transition>
      <confirm-side-dialog
        v-if="waiting"
        v-on:cancel="closeWaitingForm"
        v-bind:need-confirm="needConfirmWaitingForm"
        v-bind:label="waitingListFormTitle">
        <form-add-to-waiting-list
          v-bind:favorite-services="servicesWithFavorites.favorites"
          v-bind:has-create-patients="hasCreatePatients"
          v-bind:has-view-patient-phone="hasViewPatientContacts"
          v-bind:input-errors="waitingListInputErrors"
          v-bind:is-await-waAiting-request="isAwaitWaitingRequest"
          v-bind:is-loading-patient-list="isLoadingPatientList"
          v-bind:patient-list="patientList"
          v-bind:service-list="servicesWithFavorites.all"
          v-bind:waiting="waiting"
          v-on:cancel="closeWaitingForm"
          v-on:change-values="onChangeFieldValues"
          v-on:submit-exist="onSubmitWaitingExistPatient"
          v-on:submit-new="onSubmitWaitingNewPatient"
          v-on:update-patient-search-query="onSearchPatients" />
      </confirm-side-dialog>
    </side-slide-transition>
    <div class="waiting-list__table">
      <div class="waiting-list__table__head">
        <input-sort
          v-on:input="sortWaitingList('patientName', $event)"
          name="patientname"
          v-model="patientNameSort"
        >
          <div class="waiting-list__table__cell sort">Пациент</div>
        </input-sort>
        <input-sort
          v-on:input="sortWaitingList('phone', $event)"
          name="phone"
          v-model="phoneSort"
        >
          <div class="waiting-list__table__cell sort">Телефон</div>
        </input-sort>
        <input-sort
          v-on:input="sortWaitingList('date', $event)"
          name="dateStarts"
          v-model="dateStartsSort"
        >
          <div class="waiting-list__table__cell sort">Желаемая дата</div>
        </input-sort>
        <div class="waiting-list__table__cell">Желаемое время</div>
        <input-sort
          v-on:input="sortWaitingList('employeeName', $event)"
          name="employeeName"
          v-model="employeeNameSort"
        >
          <div class="waiting-list__table__cell sort">Врач</div>
        </input-sort>
        <div class="waiting-list__table__cell">Услуги</div>
        <div class="waiting-list__table__cell">Примечание</div>
        <div class="waiting-list__table__cell">Статус</div>
      </div>
      <button
        v-for="(waiting, key) in resultWaitingList"
        v-bind:key="key"
        class="waiting-list__table__row"
        v-on:click="onViewWaiting(waiting.id)"
        type="button"
        :ref="`scroll-id-${waiting.id}`"
      >
        <div data-label="Пациент" class="waiting-list__table__cell">{{ waiting.patientName }}</div>
        <div data-label="Телефон" class="waiting-list__table__cell">{{ waiting.phone || '-' }}</div>
        <div data-label="Желаемая дата" class="waiting-list__table__cell">{{ waiting.date }}</div>
        <div data-label="Желаемое время" class="waiting-list__table__cell">{{ waiting.time }}</div>
        <div data-label="Врач" class="waiting-list__table__cell">{{ waiting.employeeName || '-' }}</div>
        <div data-label="Услуги" class="waiting-list__table__cell">
          <ul>
            <li
              v-for="(serviceName, key) in waiting.serviceNames"
              v-bind:key="key"
            >{{ serviceName }}</li>
          </ul>
        </div>
        <div data-label="Примечание" class="waiting-list__table__cell">{{ waiting.comment || '-' }}</div>
        <div data-label="Статус" class="waiting-list__table__cell">
          {{ getWaitingStatus(waiting) }}
        </div>
        <button type="button" class="waiting-list__table__cell-delete" @click.stop="dispatchDeleteWaiting(waiting.id)" />
      </button>
    </div>
  </div>
</template>

<script>

import InputText from '@/components/InputText.vue';
import InputSort from '@/components/InputSort.vue';
import { Waiting } from '@/storage/gateways/WaitingListGateway';
import FormAddToWaitingList from '@/components/FormAddToWaitingList.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import notifierMixin from '@/mixins/notifierMixin';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import debounce from 'lodash.debounce';

const WaitingForm = FormAddToWaitingList.name;

/**
 * Выжимка из appointmentMixin нужных методов
 */
const waitingFormMixin = {
  data() {
    return {
      servicesWithFavorites: {
        all: [],
        favorites: [],
      },
      waiting: null,
      waitingListInputErrors: {},
      /**
       * @description Необходимо подтверждение перед закрытием формы.
       * @type {any}
       */
      needConfirmBeforeClosed: {
        FormAddToWaitingList: false,
      },
      /**
       * @description Ожидание завершения запроса к листу ожидания.
       * @type {boolean}
       */
      isAwaitWaitingRequest: false,
      /**
       * @description Загрузка списка пациентов.
       * @type {boolean}
       */
      isLoadingPatientList: false,
      patientList: [],
      selectedWaitingId: null,
    };
  },
  computed: {
    needConfirmWaitingForm() {
      return this.needConfirmBeforeClosed[WaitingForm];
    },
    /**
     * @description Заголовок формы добавления записи.
     * @returns {string}
     */
    waitingListFormTitle: ({ waiting }) => {
      if (waiting && waiting.id !== null) {
        return 'Запись из листа ожидания';
      }
      return 'Новая запись в лист ожидания';
    },
  },
  created() {
    this.fetchServiceFavoriteList();
    this.fetchPatientList();
  },
  methods: {
    /**
     * @description Получить список пациентов.
     * @param {string|null}
     * @returns {any[]}
     */
    fetchPatientList(searchQuery = null) {
      this
        .$appointmentScheduleGateway
        .getPatientListFromAppointment(searchQuery)
        .then((patients) => {
          patients.fold([])((list) => {
            this.patientList = list.map((patient) => ({
              ...patient,
              text: `${patient.surnameWithInitials} ${patient.phone} ${patient.medcard}`,
            }));
          });
        })
        .finally(() => { this.isLoadingPatientList = false; });
    },
    /**
     * @description Обработать создание новой записи в листе ожидания.
     * @returns {void}
     */
    onCreateWaiting() {
      this.fetchPatientList();
      this.waitingListInputErrors = {};
      this.waiting = new Waiting();
    },
    /**
     * @description Обработать просмотр записи из листа ожидания.
     * @param {number} id Идентификатор.
     * @returns {void}
     */
    async onViewWaiting(id) {
      this.selectedWaitingId = id;
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const onSuccess = (waiting) => {
        this.waiting = waiting;
      };
      this.fetchPatientList();
      const either = await this.$waitingListGateway.getById(id);
      either.fold(onFail, onSuccess);
    },
    /**
     * @description Обработать удаление записи из листа ожидания.
     * @param {number} id Идентификатор.
     * @returns {void}
     */
    async onDeleteWaiting(id) {
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const onSuccess = (response) => {
        this.notifySuccess(response.message);
        this.fetchWaitingList(true);
      };
      const either = await this.$waitingListGateway.delete(id);
      either.fold(onFail, onSuccess);
    },
    /**
     * @description Отправить событие удаления записи.
     * @param {number} id
     * @returns {void}
     */
    async dispatchDeleteWaiting(id) {
      const dialog = await this.showRemoveDialog('Удалить запись из листа ожидания?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      this.onDeleteWaiting(id);
    },
    /**
     * @description Закрыть форму добавление записи.
     * @returns {void}
     */
    closeWaitingForm() {
      this.waitingListInputErrors = {};
      this.selectedWaitingId = null;
      this.waiting = null;
      this.patientList = [];
    },
    debounceFetchPatientList: debounce(
      function _(searchQuery) { this.fetchPatientList(searchQuery); },
      300,
    ),
    /**
     * @description Получить услуги со списком избранных.
     * @returns {{all:ServiceItem[], favorites:Service[]}}
     */
    fetchServiceFavoriteList() {
      this.$serviceGateway.allWithFavoritesForView().then((list) => {
        list.fold({ all: [], favorites: [] })((services) => {
          this.servicesWithFavorites = services;
        });
      });
    },
    /**
     * @description Обработать изменение значений полей формы.
     * @param {string}  form     Название формы.
     * @param {boolean} isChange Есть изменения.
     * @returns {void}
     */
    onChangeFieldValues(form, isChange) {
      this.needConfirmBeforeClosed[form] = isChange;
    },
    /**
     * @description Обработать поиск пациентов.
     * @param {string|null} searchQuery
     * @returns {void}
     */
    onSearchPatients(searchQuery) {
      this.isLoadingPatientList = true;
      this.debounceFetchPatientList(searchQuery);
    },
    /**
     * @description Обработать запрос на добавление в лист ожидания нового пациента.
     * @param {WaitingNewPatientRequest} request Запрос.
     * @returns {void}
     */
    onSubmitWaitingNewPatient(request) {
      const onResponse = (result) => {
        this.isAwaitWaitingRequest = false;
        result.forEach(({ message }) => {
          this.closeWaitingForm();
          this.notifySuccess(message);
          this.fetchWaitingList();
          this.fetchPatientList();
        });
        result.forEachFail((error) => {
          this.waitingListInputErrors = Object.fromEntries(error.causes) || {};
          if (error.causes.length === 0) {
            this.notifyFail(error.title);
          }
        });
      };
      this.isAwaitWaitingRequest = true;
      const saving = this.selectedWaitingId !== null
        ? this.$waitingListGateway.editWithNewPatient(request, this.selectedWaitingId)
        : this.$waitingListGateway.createWithNewPatient(request);
      saving.then(onResponse);
    },
    /**
     * @description Обработать запрос на добавление в лист ожидания существующего пациента.
     * @param {WaitingExistPatientRequest} request Запрос.
     * @returns {void}
     */
    onSubmitWaitingExistPatient(request) {
      const onResponse = (result) => {
        this.isAwaitWaitingRequest = false;
        result.forEach(({ message }) => {
          this.closeWaitingForm();
          this.notifySuccess(message);
          this.fetchWaitingList();
          this.fetchPatientList();
        });
        result.forEachFail((error) => {
          this.waitingListInputErrors = Object.fromEntries(error.causes) || {};
          if (error.causes.length === 0) {
            this.notifyFail(error.title);
          }
        });
      };
      this.isAwaitWaitingRequest = true;
      const saving = this.selectedWaitingId !== null
        ? this.$waitingListGateway.editWithExistPatient(request, this.selectedWaitingId)
        : this.$waitingListGateway.createWithExistPatient(request);
      saving.then(onResponse);
    },
  },
};

export default {
  components: {
    InputSort,
    InputText,
    FormAddToWaitingList,
    ConfirmSideDialog,
    SideSlideTransition,
  },
  mixins: [waitingFormMixin, notifierMixin, builtInDialogMixin],
  data() {
    return {
      /**
       * @description Допустимые действия.
       * @type {string[]}
       */
      actions: [],
      /**
       * Лист ожидания
       */
      waitingList: [],
      /**
       * Сортированный лист ожидания
       */
      sortedWaitingList: [],
      /**
       * Поисковой запрос
       */
      searchQuery: '',
      /**
       * Состояние сортировки по имени пациента
       */
      patientNameSort: '',
      /**
       * Состояние сортировки по имени работника
       */
      employeeNameSort: '',
      /**
       * Состояние сортировки по телефону
       */
      phoneSort: '',
      /**
       * Состояние сортировки по дате
       */
      dateStartsSort: '',
    };
  },
  computed: {
    /**
     * @description Может создавать пациентов.
     * @type {boolean}
     */
    hasCreatePatients() {
      return this.actions.includes('create-patients');
    },
    /**
     * @description Может взаимодействовать с контактами пациента.
     * @type {boolean}
     */
    hasViewPatientContacts() {
      return this.actions.includes('view-patient-contacts');
    },
    resultWaitingList({ sortedWaitingList, searchQuery }) {
      if (!searchQuery.length) {
        return sortedWaitingList;
      }
      const query = searchQuery.toLowerCase();
      return sortedWaitingList.filter(
        (waiting) => waiting.employeeName.toLowerCase().includes(query)
            || waiting.phone.toLowerCase().includes(query)
            || waiting.patientName.toLowerCase().includes(query)
            || waiting.date.toLowerCase().includes(query)
            || waiting.serviceNames.filter((name) => name.toLowerCase().includes(query)).length,
      );
    },
  },
  created() {
    this.fetchWaitingList();
  },
  updated() {
    this.scrollByLink();
  },
  methods: {
    /**
     * Скролим до нужной записи в списке
     *
     * @returns {void}
     */
    scrollByLink() {
      const scrollId = this.$route.query.scroll_id;
      const element = this.$refs[`scroll-id-${scrollId}`];
      if (element) {
        element[0].scrollIntoView({ behavior: 'smooth' });
        element[0].classList.remove('waiting-list__highlight');
        setTimeout(() => element[0].classList.add('waiting-list__highlight'), 0);
      }
    },
    /**
     * Очистить состояние сортировок
     * @param exception Исключение, которое не чистим
     */
    clearSorts(exception = '') {
      if (exception !== 'patientName') {
        this.patientNameSort = '';
      }
      if (exception !== 'employeeName') {
        this.employeeNameSort = '';
      }
      if (exception !== 'phone') {
        this.phoneSort = '';
      }
      if (exception !== 'date') {
        this.dateStartsSort = '';
      }
    },
    /**
     * @description Получить лист ожидания.
     * @returns {void}
     */
    fetchWaitingList() {
      this.$waitingListGateway.getWaitingList().then((result) => {
        this.waitingList = result.map(({ list }) => list).orSome([]);
        this.actions = result.map(({ actions }) => actions).orSome([]);
        this.sortedWaitingList = [...this.waitingList];
      });
    },
    /**
     * Сортировать список ожидания
     * @param sortType
     * @param direction
     */
    sortWaitingList(sortType, direction) {
      this.clearSorts(sortType);
      if (direction === 'desc' || direction === 'asc') {
        this.sortedWaitingList = this.sortedWaitingList.sort((waiting1, waiting2) => {
          let sort1;
          let sort2;
          if (direction === 'desc') {
            sort1 = waiting1[sortType];
            sort2 = waiting2[sortType];
          } else {
            sort2 = waiting1[sortType];
            sort1 = waiting2[sortType];
          }
          if (sortType === 'date') {
            if (sort1.includes('-')) {
              sort1 = sort1.split('-')[0].trim();
            }
            if (sort2.includes('-')) {
              sort2 = sort2.split('-')[0].trim();
            }
            sort1 = sort1.split('.').reduce((acc, value) => `${value}.${acc}`);
            sort2 = sort2.split('.').reduce((acc, value) => `${value}.${acc}`);
          }
          if (sort1 > sort2) {
            return 1;
          }
          if (sort1 < sort2) {
            return -1;
          }
          return 0;
        });
      } else {
        this.sortedWaitingList = [...this.waitingList];
      }
    },
    /**
     * @description Получить статус записи из листа ожидания.
     * @param {Object} waiting Запиись из листа ожидания.
     * @returns {string}
     */
    getWaitingStatus(waiting) {
      return waiting.status === 'appointed' ? 'Назначен' : '';
    },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.waiting-list {
  padding: 38px 20px 48px 23px;
  width: 100%;
  overflow: auto;
  &__highlight {
    animation: hanim 3s;
  }
  @keyframes hanim {
    from {
      background: #c6c7c8;
    }
    to {
      background: #ffffff;
    }
  }
  &__header {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    margin: 0 0 20px 0;
  }
  &__button {
    @include buttons.btn-blue(200px, 48px, 16px);
    @include buttons.btn-blue-add(19px);
  }
  &__search {
    max-width: 500px;
    position: relative;
    &:before {
      position: absolute;
      content: '';
      right: 13px;
      top: 8px;
      cursor: pointer;
      background-repeat: no-repeat;
      background-image: url("@/assets/img/search-blue.svg");
      background-size: contain;
      width: 24px;
      height: 24px;
    }
    .text__input {
      padding: 0 35px 0 10px;
    }
  }
  &__table {
    counter-reset: counterEmployees;
    margin: 40px 0 0 0;
    &__head,
    &__row {
      width: 100%;
      text-align: left;
      align-items: center;
      cursor: pointer;
      display: grid;
      font-size: 14px;
      grid-template-columns: 150px 150px 200px 150px 250px 1fr 1fr 1fr 12px;
      line-height: 17px;
    }
    &__head {
      font-weight: 700;
      border-bottom: 2px solid #0489e0;
      padding: 10px 0;
    }
    &__row {
      border-bottom: 1px solid #eaeff3;
      padding: 12px 0;
      &:hover {
        background: #eaeff3;
      }
    }
    &__cell {
      padding: 0 8px;
      cursor: auto;
      ul {
        padding-left: 15px;
      }
      &.sort {
        text-align: left;
        cursor: pointer;
        display: flex;
        align-items: center;
        &:after {
          content: '';
          background-image: url("@/assets/img/checked/checked-grey.svg");
          background-repeat: no-repeat;
          background-size: contain;
          background-position: center;
          width: 10px;
          height: 10px;
          margin: 0 0 0 5px;
        }
        &:hover {
          text-decoration: none;
        }
      }
      &-delete {
        display: block;
        align-self: center;
        background-image: url("@/assets/img/exit-grey.svg");
        background-size: contain;
        background-repeat: no-repeat;
        width: 12px;
        height: 12px;
        margin-left: auto;
        margin-right: 12px;
        cursor: pointer;
        &:hover {
          background-image: url("@/assets/img/exit-blue.svg");
        }
      }
    }
  }
}
@media screen and (max-width: 1460px) {
  .waiting-list {
    &__table {
      margin: 25px 0 0 0;
      &__head {
        display: none;
      }
      &__row {
        grid-template-columns: 1fr;
        grid-gap: 10px;
        margin: 0 0 20px 0;
      }
      &__cell {
        display: flex;
        justify-content: space-between;
        text-align: right;
        ul {
          text-align: left;
          padding-left: 24px;
        }
        &-delete {
          order: -1;
        }
      }
      &__cell:before {
        content: attr(data-label);
        font-weight: 700;
        margin-right: 20px;
      }
    }
  }
}
@media screen and (max-width: 640px) {
  .waiting-list {
    padding: 38px 10px 140px;
  }
}
</style>
